import { useDateRangePickerState as originalUseDateRangePickerState } from '@react-stately/datepicker';
import type {
  DateRangePickerStateOptions as OriginalDateRangePickerOptions,
  DateRangePickerState as OriginalDateRangePickerState,
} from '@react-stately/datepicker';
import { DateValue } from '@internationalized/date';
import { useRef } from 'react';
import { rangeContainsUnavailableDate } from './helpers';
import { DateRangePickerStateKeys } from '../../utils/types';

type OriginalOnChange<T extends DateValue> = NonNullable<
  OriginalDateRangePickerOptions<T>['onChange']
>;

export type DateRangePickerStateOptions<
  T extends DateValue,
  P extends string,
  O extends OriginalOnChange<T> = OriginalOnChange<T>
> = Pick<
  OriginalDateRangePickerOptions<T>,
  'allowsNonContiguousRanges' | 'isDateUnavailable'
> & {
  // We allow clearing the DateRangeField and thus have to allow a null value
  // which is currently not part of the DateRangePickerProps type:
  // https://unpkg.com/browse/@react-types/datepicker@3.5.0/src/index.d.ts#:~:text=ValueBase%3CRangeValue%3CT%3E%20%7C%20null%2C%20RangeValue%3CMappedDateValue%3CT%3E%3E
  onChange?: (
    value: Parameters<O>[0] | null,
    predefinedRange?: P
  ) => ReturnType<O>;
};

type OriginalSetDate = OriginalDateRangePickerState['setDateRange'];

export type DateRangePickerState<P extends string> = Pick<
  OriginalDateRangePickerState,
  DateRangePickerStateKeys
> & {
  setDateRange: (
    value: Parameters<OriginalSetDate>[0],
    predefinedRange?: P
  ) => ReturnType<OriginalSetDate>;
};

/**
 * A wrapper to allow for the onChange callback to have extra info
 **/
export const useDateRangePickerState = <P extends string>({
  onChange,
  ...options
}: DateRangePickerStateOptions<DateValue, P>): DateRangePickerState<P> => {
  const lastPredefinedRange = useRef<P | undefined>(undefined);

  const state = originalUseDateRangePickerState({
    ...options,
    validate: (value) => {
      return !options.allowsNonContiguousRanges &&
        rangeContainsUnavailableDate(value, options.isDateUnavailable)
        ? 'error'
        : true;
    },
    onChange: (value) => {
      onChange?.(value, lastPredefinedRange.current);
      lastPredefinedRange.current = undefined;
    },
  });

  return {
    ...state,
    setDateRange: (value, predefinedRange) => {
      lastPredefinedRange.current = predefinedRange;
      state.setDateRange(value);
    },
  };
};
